import { useState } from 'react';
import { Plus } from 'lucide-react';
import { FormDialog } from '@/components/dialog/FormDialog';
import { Field } from '@/components/form/Field';
import { useSubmit } from '@/hooks/useSubmit';
import { COMPANY_ROLE } from '@/models/references/references';
import type UserModel from '@/models/entities/UserModel';
import { UserFormDialog } from '@/pages/users/UserFormDialog';
import type { CompanyOption } from '@/stores/useCompanyStore';
import { useAuthStore } from '@/stores/useAuthStore';
import { ROLE_ADMIN } from '@/security/navigation';
import { CompanyRoleField } from './CompanyRoleField';
import { createMember } from './members';
import { TeamField } from './TeamField';
import { UserCombobox } from './UserCombobox';

/**
 * Ajout d'un employé à la société sélectionnée (POST /members) par un Dirigeant, depuis la liste des employés :
 * choix d'un utilisateur (combobox, ou bouton + pour en créer un si l'on est aussi admin), de son rôle dans la société
 * et, au besoin, de son équipe (TeamField).
 */
export function AddEmployeeDialog({
  company,
  onClose,
  onSaved,
}: {
  company: CompanyOption;
  onClose: () => void;
  /** teamError : motif si l'ajout à l'équipe a été refusé (l'employé est tout de même ajouté à la société) */
  onSaved: (personName: string, teamError: string | null) => void;
}) {
  const [selected, setSelected] = useState<UserModel | null>(null);
  const [companyRole, setCompanyRole] = useState<string>(COMPANY_ROLE.EMPLOYEE);
  const [teamId, setTeamId] = useState<number | null>(null);
  /** Création d'un utilisateur en cours (formulaire utilisateur, vide) */
  const [creating, setCreating] = useState(false);
  const { submit, submitting, error } = useSubmit();
  // Créer un utilisateur est un droit admin (POST /users)
  const canCreateUser = useAuthStore((state) => !!state.user?.roles.includes(ROLE_ADMIN));

  const save = () =>
    submit(async () => {
      if (!selected) return;
      const teamError = await createMember(selected.id, company.id, companyRole, teamId);
      onSaved(selected.fullName, teamError);
    });

  return (
    <>
      <FormDialog
        open={!creating}
        title={`Nouvel employé chez ${company.name}`}
        description="Choisissez un utilisateur, son rôle dans la société et, au besoin, son équipe."
        submitLabel="Ajouter"
        submitIcon={Plus}
        canSubmit={!!selected}
        submitting={submitting}
        error={error}
        onSubmit={save}
        onClose={onClose}
      >
        <Field id="employee-user" label="Utilisateur" required>
          <UserCombobox
            id="employee-user"
            companyId={company.id}
            value={selected}
            onChange={setSelected}
            onCreate={canCreateUser ? () => setCreating(true) : undefined}
          />
        </Field>
        <CompanyRoleField id="employee-role" value={companyRole} onChange={setCompanyRole} />
        <TeamField id="employee-team" companyId={company.id} value={teamId} onChange={setTeamId} />
      </FormDialog>

      {/* Utilisateur introuvable : création (formulaire habituel), puis retour à l'ajout avec lui sélectionné */}
      {creating && (
        <UserFormDialog
          user={null}
          onClose={() => setCreating(false)}
          onSaved={(user) => {
            setSelected(user);
            setCreating(false);
          }}
        />
      )}
    </>
  );
}
